import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { SCOPE_TOKEN_FIELD } from "../contexts/AuthContext";
import { handOffPendingDocs } from "./RegisterStatus";
import { maskDigits, maskAgenciaBR, maskContaBR, finalizeBankIdentBR } from "../lib/numberFormat";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Building2, Users, Eye, EyeOff, KeyRound, ArrowLeft
} from "lucide-react";

function cnpjMask(v) {
  const d = v.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function phoneMask(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState(null);
  const [nome, setNome] = useState("");
  const [doc, setDoc] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [banco, setBanco] = useState("");
  const [agencia, setAgencia] = useState("");
  const [conta, setConta] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitRef = useRef(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitRef.current) return; // prevent double-submit
    if (!tipo) { toast.error("Selecione o tipo de conta"); return; }
    if (password.length < 8) { toast.error("Senha deve ter pelo menos 8 caracteres"); return; }

    const cleanDoc = doc.replace(/\D/g, "");
    if (cleanDoc.length !== 14 && cleanDoc.length !== 11) {
      toast.error("CNPJ deve ter 14 dígitos ou CPF 11 dígitos");
      return;
    }

    submitRef.current = true;
    setSubmitting(true);
    try {
      const idempotencyKey = `reg_${cleanDoc}_${Date.now()}`;
      const payload = {
        tipo: tipo === "parceiro" ? "originador" : "cedente",
        nome: nome.trim(),
        doc: cleanDoc,
        email: email.trim().toLowerCase(),
        password,
        telefone: telefone.replace(/\D/g, "") || undefined,
        idempotency_key: idempotencyKey,
      };
      if (banco || agencia || conta) {
        payload.dados_bancarios = { banco, agencia, conta };
      }

      const { data } = await api.post("/auth/register", payload);
      if (!data.user_id) {
        // Resposta genérica do backend (fluxo de CNPJ já cadastrado, anti-enumeração):
        // não há cadastro novo para acompanhar — mostra a mensagem e volta ao login.
        toast.success(data.message || "Recebemos sua solicitação.");
        navigate("/login");
        return;
      }
      toast.success("Cadastro enviado para aprovação!");
      // docs-cadastro (Escopo 1/2): o passo dos documentos obrigatórios só
      // existe com a registration do BO devolvida E o token de escopo do
      // register. O token vai à tela de status SÓ em memória do módulo
      // (revisão Astra L1): nunca pelo state do router, que passa por
      // `history.pushState`. O CPF/CNPJ não vai: o GET autenticado devolve
      // `person_type`. Sem eles: tela de status como hoje.
      handOffPendingDocs(data.user_id,
        data.registration_id ? data[SCOPE_TOKEN_FIELD] : null);
      navigate(`/cadastro/status/${data.user_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      submitRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[460px]">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center font-bold">Z</div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight">Portal Zemo</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mt-1">Novo cadastro</div>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Criar conta</h1>
          <p className="mt-1.5 text-sm text-zinc-500">
            Preencha seus dados. Após envio, o Backoffice da Zemo validará e aprovará seu acesso.
          </p>

          {/* Tipo selector */}
          {!tipo ? (
            <div className="mt-8 space-y-3" data-testid="register-tipo-selector">
              <p className="text-sm font-medium text-zinc-700">Selecione o tipo de conta:</p>
              <button
                onClick={() => setTipo("parceiro")}
                className="w-full p-4 rounded-lg border-2 border-zinc-200 hover:border-[#0055FF] transition-colors text-left flex items-start gap-3"
                data-testid="register-tipo-parceiro"
              >
                <Building2 className="h-5 w-5 text-[#0055FF] mt-0.5" />
                <div>
                  <div className="font-semibold text-zinc-900">Parceiro (Originador)</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Finders, Parceiros que trazem operações, sejam via API ou manualmente subindo elas nesse portal</div>
                </div>
              </button>
              <button
                onClick={() => setTipo("cedente")}
                className="w-full p-4 rounded-lg border-2 border-zinc-200 hover:border-[#0055FF] transition-colors text-left flex items-start gap-3"
                data-testid="register-tipo-cedente"
              >
                <Users className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-zinc-900">Cedente</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Empresa que cede recebíveis para antecipação</div>
                </div>
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
              <div className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => setTipo(null)} className="text-zinc-400 hover:text-zinc-600" data-testid="register-back-tipo">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  {tipo === "parceiro" ? "Parceiro (Originador)" : "Cedente"}
                </span>
              </div>

              <div className="space-y-1.5">
                <Label>Razão Social</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da empresa" required data-testid="register-nome" />
              </div>

              <div className="space-y-1.5">
                <Label>CNPJ</Label>
                <Input value={doc} onChange={(e) => setDoc(cnpjMask(e.target.value))} placeholder="00.000.000/0000-00" required data-testid="register-doc" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>E-mail</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contato@empresa.com.br" required data-testid="register-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefone</Label>
                  <Input value={telefone} onChange={(e) => setTelefone(phoneMask(e.target.value))} placeholder="(11) 99999-0000" data-testid="register-telefone" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Senha de acesso</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres" required minLength={8}
                    className="pr-10" data-testid="register-password"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Dados bancários (opcional) */}
              <details className="group">
                <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-700 font-medium" data-testid="register-bank-toggle">
                  Dados bancários (opcional)
                </summary>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Banco</Label>
                    <Input inputMode="numeric" value={banco} onChange={(e) => setBanco(maskDigits(e.target.value, banco, 3))} placeholder="001" data-testid="register-banco" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Agência</Label>
                    <Input inputMode="text" value={agencia} onChange={(e) => setAgencia(maskAgenciaBR(e.target.value, agencia))} onBlur={(e) => setAgencia(finalizeBankIdentBR(e.target.value))} placeholder="0001-2" data-testid="register-agencia" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Conta</Label>
                    <Input inputMode="text" value={conta} onChange={(e) => setConta(maskContaBR(e.target.value, conta))} onBlur={(e) => setConta(finalizeBankIdentBR(e.target.value))} placeholder="12345-6" data-testid="register-conta" />
                  </div>
                </div>
              </details>

              <Button type="submit" disabled={submitting}
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
                data-testid="register-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enviar cadastro <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[#0055FF] hover:underline font-medium" data-testid="register-login-link">
              Já tenho conta — fazer login
            </Link>
          </div>

          <div className="mt-8 flex items-center gap-2 text-[11px] text-zinc-400">
            <KeyRound className="h-3 w-3" />
            <span>Seus dados são criptografados e protegidos pela LGPD</span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="hidden lg:block relative bg-[#0a0a0a] overflow-hidden">
        <div className="absolute inset-0 zemo-grid-bg opacity-60"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0055FF]/30"></div>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-zinc-400">
            ZemoBank · Cadastro
          </div>
          <div>
            <div className="text-5xl leading-[0.95] font-semibold tracking-tight">
              Comece a<br />
              <span className="text-[#5b8cff]">antecipar</span><br />
              hoje.
            </div>
            <p className="mt-6 max-w-md text-zinc-400 text-[15px] leading-relaxed">
              Cadastre sua empresa, aguarde a aprovação e tenha acesso ao
              portal completo de antecipação de recebíveis.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-8 pt-10 border-t border-white/10">
            <div>
              <div className="text-2xl font-semibold tracking-tight">1.</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">Cadastre-se</div>
            </div>
            <div>
              <div className="text-2xl font-semibold tracking-tight">2.</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">Aprovação</div>
            </div>
            <div>
              <div className="text-2xl font-semibold tracking-tight">3.</div>
              <div className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">Acesse o portal</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
